// Audit Trail — immutable, read-only feed of every mutation across SITROOM.
import React, { useMemo, useState } from "react";
import axios from "axios";
import { Clock, Search, Download, ChevronLeft, ChevronRight, ChevronDown, ExternalLink } from "lucide-react";
import { useStore } from "../store";
import { useAuditLogs } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color map for action_type badges
const ACTION_STYLE = {
  CREATE: { bg: "rgba(94,163,227,0.14)", border: "rgba(94,163,227,0.40)", color: "#5fa3e3" },
  UPDATE: { bg: "rgba(243,177,74,0.14)", border: "rgba(243,177,74,0.40)", color: "#f3b14a" },
  DELETE: { bg: "rgba(226,75,74,0.14)", border: "rgba(226,75,74,0.40)", color: "#E24B4A" },
  STATUS_CHANGE: { bg: "rgba(200,154,219,0.14)", border: "rgba(200,154,219,0.40)", color: "#c89adb" },
  AI_GENERATED: { bg: "rgba(78,208,132,0.14)", border: "rgba(78,208,132,0.40)", color: "#4ed084" },
};

const ENTITY_LABEL = {
  incident: "Incident",
  eightd: "8D",
  fmea: "FMEA",
  lpa: "LPA",
  ai: "AI",
};

const FILTERS = [
  { key: "all", label: "All", entity: "", action_type: "" },
  { key: "incident", label: "Incidents", entity: "incident", action_type: "" },
  { key: "eightd", label: "8D", entity: "eightd", action_type: "" },
  { key: "fmea", label: "FMEA", entity: "fmea", action_type: "" },
  { key: "lpa", label: "LPA", entity: "lpa", action_type: "" },
  { key: "ai", label: "AI Actions", entity: "", action_type: "AI_GENERATED" },
];

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

function summaryText(row) {
  const p = row.payload || {};
  if (row.action === "create") {
    if (p.title) return `Created "${p.title}"`;
    if (p.sourceIncident) return `From ${p.sourceIncident}`;
    return "Created";
  }
  if (row.action === "comment") return `Commented by ${p.author || "—"}`;
  if (row.action === "patch") {
    const keys = (p.keys || []).join(", ");
    return keys ? `Updated ${keys}` : "Updated";
  }
  if (row.action === "ai_generated") return `${p.endpoint || "AI"} · ${p.model || "claude"}`;
  return row.action;
}

function exportCsv(rows) {
  const headers = ["Timestamp", "Actor", "Role", "Action", "Entity", "Entity ID", "Tenant", "Summary"];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  rows.forEach((r) => {
    lines.push(
      [
        r.at,
        r.actor_name,
        r.actor_role,
        r.action_type || r.action,
        r.entity,
        r.entity_id,
        r.tenant_id,
        summaryText(r),
      ]
        .map(escape)
        .join(","),
    );
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sitroom-audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const DiffPanel = ({ before = {}, after = {}, index }) => {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v); } catch { return String(v); }
  };
  return (
    <div
      className="px-4 py-3"
      style={{
        background: "rgba(255,255,255,0.02)",
        borderTop: "1px solid rgba(255,255,255,0.04)",
      }}
      data-testid={`audit-diff-panel-${index}`}
    >
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: "120px 1fr 1fr",
          fontFamily: "'DM Mono', monospace",
          fontSize: 10,
        }}
      >
        <div style={{ color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase" }}>Field</div>
        <div style={{ color: "#E24B4A", letterSpacing: "1.2px", textTransform: "uppercase" }}>− Before</div>
        <div style={{ color: "#4ed084", letterSpacing: "1.2px", textTransform: "uppercase" }}>+ After</div>
        {keys.map((k) => (
          <React.Fragment key={k}>
            <div style={{ color: "#9ec3ec" }}>{k}</div>
            <div
              className="rounded px-2 py-1"
              style={{
                background: "rgba(226,75,74,0.06)",
                border: "1px solid rgba(226,75,74,0.25)",
                color: "#f0c4c4",
                wordBreak: "break-word",
              }}
              data-testid={`audit-diff-before-${index}-${k}`}
            >
              {fmt(before?.[k])}
            </div>
            <div
              className="rounded px-2 py-1"
              style={{
                background: "rgba(78,208,132,0.06)",
                border: "1px solid rgba(78,208,132,0.25)",
                color: "#c4f0d4",
                wordBreak: "break-word",
              }}
              data-testid={`audit-diff-after-${index}-${k}`}
            >
              {fmt(after?.[k])}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const Badge = ({ type }) => {
  const style = ACTION_STYLE[type] || { bg: "rgba(145,152,176,0.12)", border: "rgba(145,152,176,0.30)", color: "#9198b0" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        letterSpacing: "1.2px",
        textTransform: "uppercase",
      }}
      data-testid={`audit-badge-${type}`}
    >
      {type}
    </span>
  );
};

export default function AuditTrail() {
  const openIncident = useStore((s) => s.openPanel);
  const openEightD = useStore((s) => s.openEightDPanel);
  const openFmea = useStore((s) => s.openFmeaPanel);
  const openLpa = useStore((s) => s.openLpaPanel);

  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [expanded, setExpanded] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const filter = FILTERS.find((f) => f.key === active) || FILTERS[0];

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (filter.entity) p.entity = filter.entity;
    if (filter.action_type) p.action_type = filter.action_type;
    if (search.trim()) {
      // Search applies to actor OR entity_id — try entity_id first; UI can be improved later
      const q = search.trim();
      if (/^(INC|FMEA|LPA|8D)/i.test(q)) p.entity_id = q;
      else p.actor = q;
    }
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [filter.entity, filter.action_type, search, dateFrom, dateTo, page]);

  const { data, isLoading } = useAuditLogs(params);
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const handleRowClick = (row) => {
    if (row.entity === "incident") openIncident(row.entity_id);
    else if (row.entity === "eightd") openEightD(row.entity_id);
    else if (row.entity === "fmea") openFmea(row.entity_id);
    else if (row.entity === "lpa") openLpa(row.entity_id);
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const exportParams = { ...params, export: true };
      delete exportParams.page;
      delete exportParams.page_size;
      const resp = await axios.get(`${API}/audit-logs`, {
        params: exportParams,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sitroom-audit-trail-full-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // surface error inline — could pipe to toast in future
       
      console.error("Export-all failed", e);
    } finally {
      setExportingAll(false);
    }
  };

  return (
    <div className="p-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="flex items-center gap-2">
            <Clock size={18} style={{ color: "#9ec3ec" }} />
            <h1
              data-testid="view-title"
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: 28,
                fontWeight: 600,
                color: "var(--text)",
                letterSpacing: "-0.3px",
              }}
            >
              Audit Trail
            </h1>
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              color: "#9198b0",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            Immutable audit record · IATF 16949 compliant · ISO 9001 §7.5.3
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportCsv(rows)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#c5cad8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              letterSpacing: "1px",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#185FA5")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
            data-testid="audit-export-csv-btn"
          >
            <Download size={13} />
            This page
          </button>
          <button
            onClick={handleExportAll}
            disabled={exportingAll}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
            style={{
              background: exportingAll ? "rgba(24,95,165,0.20)" : "#185FA5",
              border: "1px solid #185FA5",
              color: "#fff",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              letterSpacing: "1px",
              textTransform: "uppercase",
              cursor: exportingAll ? "not-allowed" : "pointer",
            }}
            data-testid="audit-export-all-btn"
          >
            <Download size={13} />
            {exportingAll ? "Exporting…" : "Export all (filtered)"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mt-5 mb-4" data-testid="audit-filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => {
              setActive(f.key);
              setPage(1);
            }}
            className="px-3 py-1 rounded-md transition-colors"
            style={{
              background: active === f.key ? "#185FA5" : "var(--border)",
              border: `1px solid ${active === f.key ? "#185FA5" : "var(--border)"}`,
              color: active === f.key ? "#fff" : "#c5cad8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
            data-testid={`audit-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="px-2 py-1 rounded outline-none"
            style={{
              background: "var(--bg2)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#c5cad8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
            }}
            data-testid="audit-date-from"
          />
          <span style={{ color: "#6b7388", fontFamily: "'DM Mono', monospace", fontSize: 11 }}>→</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="px-2 py-1 rounded outline-none"
            style={{
              background: "var(--bg2)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#c5cad8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
            }}
            data-testid="audit-date-to"
          />
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3" style={{ maxWidth: 420 }}>
        <Search
          size={13}
          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#6b7388" }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search by entity ID or actor name…"
          className="w-full pl-7 pr-3 py-1.5 rounded outline-none"
          style={{
            background: "var(--bg2)",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "var(--text)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          }}
          data-testid="audit-search-input"
        />
      </div>

      {/* Table */}
      <div
        className="rounded-md overflow-hidden"
        style={{ background: "var(--bg2)", border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <div
          className="grid px-4 py-2"
          style={{
            gridTemplateColumns: "180px 200px 140px 160px 1fr 90px",
            background: "rgba(255,255,255,0.03)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            color: "#9198b0",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          <div>Timestamp</div>
          <div>Actor</div>
          <div>Action</div>
          <div>Entity</div>
          <div>Change Summary</div>
          <div style={{ textAlign: "right" }}>Actions</div>
        </div>

        {isLoading && (
          <div
            className="px-4 py-8 text-center"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0", letterSpacing: "1.5px" }}
            data-testid="audit-loading"
          >
            Loading audit log…
          </div>
        )}

        {!isLoading && rows.length === 0 && (
          <div
            className="px-4 py-8 text-center"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0", letterSpacing: "1.5px" }}
            data-testid="audit-empty"
          >
            No audit entries match your filters.
          </div>
        )}

        {rows.map((r, i) => {
          const hasDiff = r.payload && (r.payload.before || r.payload.after);
          const isOpen = expanded === i;
          const entityLink = ["incident", "eightd", "fmea", "lpa"].includes(r.entity);
          return (
            <div
              key={`${r.at}-${r.entity_id}-${i}`}
              style={{
                borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}
              data-testid={`audit-row-${i}`}
            >
              <div
                onClick={() => handleRowClick(r)}
                className="grid px-4 py-2 cursor-pointer transition-colors"
                style={{
                  gridTemplateColumns: "180px 200px 140px 160px 1fr 90px",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  color: "#c5cad8",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(24,95,165,0.06)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ color: "#9198b0" }}>{formatTs(r.at)}</div>
                <div>
                  <div style={{ color: "var(--text)" }}>{r.actor_name || "—"}</div>
                  <div style={{ color: "#6b7388", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase" }}>
                    {r.actor_role || "—"}
                  </div>
                </div>
                <div>
                  <Badge type={r.action_type || (r.action || "").toUpperCase()} />
                </div>
                <div>
                  <div style={{ color: "#9ec3ec" }}>{r.entity_id}</div>
                  <div style={{ color: "#6b7388", fontSize: 9, letterSpacing: "1px", textTransform: "uppercase" }}>
                    {ENTITY_LABEL[r.entity] || r.entity}
                  </div>
                </div>
                <div>{summaryText(r)}</div>
                <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {hasDiff && (
                    <button
                      onClick={() => setExpanded(isOpen ? null : i)}
                      className="p-1 rounded transition-colors"
                      style={{
                        background: isOpen ? "rgba(243,177,74,0.18)" : "transparent",
                        border: `1px solid ${isOpen ? "rgba(243,177,74,0.45)" : "var(--border)"}`,
                        color: isOpen ? "#f3b14a" : "#9198b0",
                        cursor: "pointer",
                      }}
                      data-testid={`audit-diff-toggle-${i}`}
                      title="Show diff"
                    >
                      <ChevronDown
                        size={12}
                        style={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                          transition: "transform 200ms",
                        }}
                      />
                    </button>
                  )}
                  {entityLink && (
                    <button
                      onClick={() => handleRowClick(r)}
                      className="flex items-center gap-1 px-1.5 py-1 rounded transition-colors"
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(94,163,227,0.40)",
                        color: "#5fa3e3",
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 9,
                        letterSpacing: "0.4px",
                        cursor: "pointer",
                      }}
                      data-testid={`audit-open-entity-${i}`}
                      title={`Open ${ENTITY_LABEL[r.entity] || r.entity}`}
                    >
                      <ExternalLink size={9} />
                      Open
                    </button>
                  )}
                </div>
              </div>
              {isOpen && hasDiff && (
                <DiffPanel before={r.payload.before} after={r.payload.after} index={i} />
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3" data-testid="audit-pagination">
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
            color: "#9198b0",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
          }}
        >
          {total} entries · page {page} of {pages}
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="p-1 rounded transition-colors"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.10)",
              color: page <= 1 ? "#3a3f52" : "#c5cad8",
              cursor: page <= 1 ? "not-allowed" : "pointer",
            }}
            data-testid="audit-prev-btn"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="p-1 rounded transition-colors"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.10)",
              color: page >= pages ? "#3a3f52" : "#c5cad8",
              cursor: page >= pages ? "not-allowed" : "pointer",
            }}
            data-testid="audit-next-btn"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
